import dayjs from 'dayjs';

import type {
  TutorDailyUsage,
  TutorDashboardReport,
  TutorUsageTimelinePoint,
} from '../types/tutorAi';
import { isUsagePeriodValid } from './usagePeriod';

/**
 * Total por dia de uma das séries diárias.
 *
 * Reduz a data ao dia (`YYYY-MM-DD`) para casar com as chaves do período mesmo se a API
 * mandar timestamp completo, descarta item com data inválida ou vazia, e soma entradas
 * repetidas na mesma data — o contrato promete um item por dia, e somar é o
 * comportamento seguro se a promessa for quebrada.
 */
function totalsByDate<T>(
  items: readonly T[],
  dateOf: (item: T) => string,
  amountOf: (item: T) => number,
) {
  const totals = new Map<string, number>();

  for (const item of items) {
    const day = dayjs(dateOf(item));
    if (!day.isValid()) continue;

    const key = day.format('YYYY-MM-DD');
    totals.set(key, (totals.get(key) ?? 0) + amountOf(item));
  }

  return totals;
}

/**
 * Dias do período, em ordem crescente, como chaves `YYYY-MM-DD`.
 *
 * As duas funções de densificação percorrem o mesmo calendário e só diferem no formato do
 * ponto que produzem — o eixo é o mesmo, então ele mora aqui e cada uma mapeia o seu ponto.
 *
 * Pressupõe período já validado por `isUsagePeriodValid`.
 */
function eachDayInPeriod(dateStart: string, dateEnd: string): string[] {
  const days: string[] = [];
  const end = dayjs(dateEnd);

  for (let day = dayjs(dateStart); !day.isAfter(end); day = day.add(1, 'day')) {
    days.push(day.format('YYYY-MM-DD'));
  }

  return days;
}

/**
 * Funde as duas séries diárias do dashboard numa timeline densa: um ponto por dia do
 * período filtrado, com zero onde a API não devolveu nada.
 *
 * A densificação não é cosmética. A API omite dias sem atividade **e omite de forma
 * independente por série**, então sem ela: (1) o eixo X mentiria sobre o tempo — 30 dias
 * com 4 pontos desenhariam 4 colunas equidistantes; (2) não haveria um array único capaz
 * de alimentar dois painéis alinhados; e (3) o crosshair sincronizado não teria o mesmo
 * índice de categoria nos dois painéis para casar a coluna. Zero é o valor semanticamente
 * correto, não "sem dado": o contrato declara que dia omitido é dia sem consumo.
 *
 * Itens fora de `[dateStart, dateEnd]` são ignorados — o recorte do gráfico é o filtro da
 * tela, não o que a API decidiu mandar.
 *
 * @param report - Consolidado já normalizado
 * @param dateStart - Início do período, ISO `YYYY-MM-DD`
 * @param dateEnd - Fim do período, ISO `YYYY-MM-DD`
 * @returns Um ponto por dia do período, em ordem crescente. Vazio se o período não passar
 * em `isUsagePeriodValid`
 *
 * @example
 * ```ts
 * buildUsageTimeline(report, '2026-07-01', '2026-07-02');
 * // [{ date: '2026-07-01', tokensUsed: 900, messages: 0 },
 * //  { date: '2026-07-02', tokensUsed: 0, messages: 4 }]
 * ```
 */
export function buildUsageTimeline(
  report: TutorDashboardReport,
  dateStart: string,
  dateEnd: string,
): readonly TutorUsageTimelinePoint[] {
  if (!isUsagePeriodValid(dateStart, dateEnd)) return [];

  const tokens = totalsByDate(
    report.dailyTokensEvolution,
    item => item.date,
    item => item.tokensUsed,
  );
  const messages = totalsByDate(
    report.dailyMessagesEvolution,
    item => item.date,
    item => item.messages,
  );

  return eachDayInPeriod(dateStart, dateEnd).map(date => ({
    date,
    tokensUsed: tokens.get(date) ?? 0,
    messages: messages.get(date) ?? 0,
  }));
}

/**
 * Densifica a série diária de tokens do relatório agregado: um ponto por dia do período
 * filtrado, com zero onde a API não devolveu nada.
 *
 * Mesma justificativa de `buildUsageTimeline` — dia omitido é dia sem consumo, e sem o
 * zero-fill o eixo X mentiria sobre o tempo, desenhando N pontos equidistantes no lugar do
 * calendário. Existe separada porque `buildUsageTimeline` funde **duas** séries e é tipada
 * para `TutorDashboardReport`; aqui só há a de tokens.
 *
 * Itens fora de `[dateStart, dateEnd]` são ignorados — o recorte do gráfico é o filtro da
 * tela, não o que a API decidiu mandar.
 *
 * @param dailyUsage - Série diária já normalizada
 * @param dateStart - Início do período, ISO `YYYY-MM-DD`
 * @param dateEnd - Fim do período, ISO `YYYY-MM-DD`
 * @returns Um ponto por dia do período, em ordem crescente. Vazio se o período não passar
 * em `isUsagePeriodValid`
 *
 * @example
 * ```ts
 * buildDailyTokenSeries([{ date: '2026-07-02', tokensUsed: 900 }], '2026-07-01', '2026-07-02');
 * // [{ date: '2026-07-01', tokensUsed: 0 }, { date: '2026-07-02', tokensUsed: 900 }]
 * ```
 */
export function buildDailyTokenSeries(
  dailyUsage: readonly TutorDailyUsage[],
  dateStart: string,
  dateEnd: string,
): readonly TutorDailyUsage[] {
  // Repete o gate do hook porque esta é a fronteira que aloca — ver TSDoc de
  // `isUsagePeriodValid`.
  if (!isUsagePeriodValid(dateStart, dateEnd)) return [];

  const tokens = totalsByDate(
    dailyUsage,
    item => item.date,
    item => item.tokensUsed,
  );

  return eachDayInPeriod(dateStart, dateEnd).map(date => ({
    date,
    tokensUsed: tokens.get(date) ?? 0,
  }));
}
